import { AcButton, AcCheckbox, AcFormCard, AcFormInput } from '@components'
import { GLOBAL_ERRORS, LABELS } from '@constants'
import { AcInputBase } from 'src/components/core/ac-input-base/ac-input-base'
import { ISandwichSubscriptionPayload } from '@typings'
import { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styles from './sandwich-subscription.module.scss'

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
] as const

const WEEKDAY_LABELS: Record<string, string> = {
  monday: 'Maandag',
  tuesday: 'Dinsdag',
  wednesday: 'Woensdag',
  thursday: 'Donderdag',
  friday: 'Vrijdag',
}

const SANDWICH_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  label: String((i + 1) * 10),
  value: (i + 1) * 10,
}))

type ISandwichSubscriptionForm = {
  onSubmit: () => void
  loading: boolean
  daysError: string | null
}

export const SandwichSubscriptionForm = ({
  onSubmit,
  loading,
  daysError,
}: ISandwichSubscriptionForm) => {
  const FormInput = AcFormInput<ISandwichSubscriptionPayload>
  const form = useFormContext<ISandwichSubscriptionPayload>()

  const minDate = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }, [])

  return (
    <div className={styles['sandwich-subscription-form']}>
      <AcFormCard title={LABELS.COMPANY_INFORMATION}>
        <FormInput
          name="company_name"
          label={LABELS.COMPANY_NAME}
          validate="required"
        />
        <FormInput
          name="invoice_email"
          label={LABELS.INVOICE_EMAIL}
          type="email"
          validate="required|email"
        />
      </AcFormCard>

      <AcFormCard title={LABELS.CONTACT_INFORMATION}>
        <FormInput
          name="first_name"
          label={LABELS.NAME_FIRST}
          validate="required"
        />
        <FormInput
          name="last_name"
          label={LABELS.NAME_LAST}
          validate="required"
        />
        <FormInput
          name="email"
          label={LABELS.EMAIL}
          type="email"
          validate="required|email"
        />
        <FormInput
          name="phone"
          label={LABELS.PHONE}
          type="tel"
          validate="required"
        />
      </AcFormCard>

      <AcFormCard title={LABELS.ADDRESS_INFORMATION}>
        <FormInput
          name="street"
          label={LABELS.STREET}
          validate="required"
        />
        <FormInput
          name="house_number"
          label={LABELS.HOUSE_NUMBER}
          type="number"
          validate="required"
        />
        <FormInput
          name="house_number_addition"
          label={LABELS.HOUSE_NUMBER_ADDITION}
        />
        <FormInput
          name="postal_code"
          label={LABELS.POSTALCODE}
          validate="required"
        />
        <FormInput
          name="city"
          label={LABELS.CITY}
          validate="required"
        />
      </AcFormCard>

      <AcFormCard title={LABELS.DELIVERY_DAYS}>
        <div className={styles['delivery-day-row']}>
          <Controller
            name="start_date"
            control={form.control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <div style={{ width: '100%' }}>
                <label htmlFor="start_date">{LABELS.START_DATE}*</label>
                <AcInputBase error={fieldState.error?.message}>
                  <input
                    className={styles['date-input']}
                    type="date"
                    id="start_date"
                    min={minDate}
                    {...field}
                  />
                </AcInputBase>
              </div>
            )}
          />
        </div>

        {WEEKDAY_KEYS.map(day => (
          <DeliveryDayRow key={day} day={day} />
        ))}

        {daysError && (
          <div className={styles['delivery-days-error']}>{daysError}</div>
        )}
      </AcFormCard>

      <div className={styles['terms-row']}>
        <Controller
          name="tac"
          control={form.control}
          rules={{ validate: v => v || LABELS.TAC_REQUIRED }}
          render={({ field, fieldState }) => (
            <div>
              <div className={styles['terms-checkbox']}>
                <AcCheckbox
                  label="Algemene voorwaarden"
                  checked={field.value}
                  onClick={() => field.onChange(!field.value)}
                />
                <span className={styles['terms-label']}>
                  Ik heb de{' '}
                  <a
                    href="https://debuurtboer.nl/algemenevoorwaarden/"
                    target="_blank"
                    rel="noopener noreferrer">
                    algemene voorwaarden
                  </a>{' '}
                  gelezen en ga akkoord
                </span>
              </div>
              {fieldState.error?.message && (
                <div className={styles['terms-error']}>
                  {fieldState.error.message}
                </div>
              )}
            </div>
          )}
        />
      </div>

      <div className={styles['submit-row']}>
        <AcButton
          label={LABELS.SUBMIT}
          onClick={onSubmit}
          disabled={loading}
        />
        {Object.keys(form.formState.errors).length > 0 && (
          <span className={styles['submit-row__error']}>
            {GLOBAL_ERRORS.FORM_STATE_INVALID}
          </span>
        )}
      </div>
    </div>
  )
}

const DeliveryDayRow = ({
  day,
}: {
  day: (typeof WEEKDAY_KEYS)[number]
}) => {
  const form = useFormContext<ISandwichSubscriptionPayload>()
  const value = useWatch({ control: form.control, name: day })
  const isChecked = value !== null && value !== undefined

  const handleToggle = () => {
    if (isChecked) {
      form.setValue(day, null, { shouldDirty: true })
    } else {
      form.setValue(day, 10, { shouldDirty: true })
    }
  }

  return (
    <div className={styles['delivery-day-row']}>
      <div className={styles['delivery-day-checkbox']}>
        <AcCheckbox
          label={WEEKDAY_LABELS[day]}
          withLabel
          checked={isChecked}
          onClick={handleToggle}
        />
      </div>
      {isChecked && (
        <div className={styles['delivery-day-select']}>
          <AcFormInput<ISandwichSubscriptionPayload>
            name={day}
            label={LABELS.NUMBER_OF_SANDWICHES}
            type="select"
            options={SANDWICH_OPTIONS}
            labelKey="label"
            valueKey="value"
            inputFormat="value"
            outputFormat="value"
            withLabel={false}
          />
        </div>
      )}
    </div>
  )
}
